<script>
import { camelCase } from 'lodash-es';
import { debounce } from 'lodash-es';

import { useUserStore } from './composables/useUserStore';

export default {
  created() {
    this.buildMenu();
  },
  data() {
    return {
      activeItem: null,
      builtMenu: [],
      isScrolling: false,
    };
  },
  methods: {
    buildMenu() {
      this.builtMenu = this.menu.reduce((accumulator, current) => {
        if (!current.children) {
          if (this.hasAuthority(current.authority)) {
            accumulator.push(current);
          }
        } else {
          const allowed = current.children.filter((child) => this.hasAuthority(child.authority));

          if (allowed.length) {
            accumulator.push({
              ...current,
              children: allowed,
              open: false,
            });
          }
        }
        return accumulator;
      }, []);
    },
    camelCase,
    handleSidebarScroll: debounce(
      function (e) {
        const target = e.target;

        if (target.scrollTop > 0) {
          this.isScrolling = true;
        } else {
          this.isScrolling = false;
        }
      },
      1000 / 60, // 60fps
      { leading: true }
    ),
    hasActiveChild(menuItem) {
      return menuItem.children?.some((child) => child.activeSidebarItemId === this.activeItem);
    },
    isEntryOpen(menuItem) {
      return menuItem.open || this.hasActiveChild(menuItem);
    },
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    },
    scrollToActiveTab() {
      setTimeout(() => {
        const element = document.getElementById(this.$route.meta.activeSidebarItemId);
        if (element && !this.isInViewport(element)) {
          element.scrollIntoView({
            behavior: 'smooth',
          });
        }
      });
    },
    selectActiveItem() {
      if (!this.$route) {
        return;
      }
      this.activeItem = this.$route.meta.activeSidebarItemId;
      this.scrollToActiveTab();
    },
    setActiveChildDropDownToOpen() {
      this.builtMenu.map((menuItem) => {
        if (this.hasActiveChild(menuItem)) {
          menuItem.open = true;
        }
      });
    },
    toggleMenu(menuItem) {
      menuItem.open = !menuItem.open;
    },
  },
  mounted() {
    this.selectActiveItem();
    this.setActiveChildDropDownToOpen();
  },
  props: {
    menu: {
      required: true,
      type: Array,
    },
  },
  setup() {
    const { hasAuthority } = useUserStore();
    return {
      hasAuthority,
    };
  },
  watch: {
    $route() {
      this.selectActiveItem();
    },
  },
};
</script>

<template>
  <div>
    <div id="sidebar" data-testid="sidebar">
      <div id="sidebar-body" data-testid="sidebarBody" class="flex flex-col space-y-2">
        <div class="my-4 flex h-16 items-center pe-8">
          <router-link to="/">
            <img class="w-[202px]" src="@assets/images/foodics-logo-purple.svg" alt="logo" />
          </router-link>
        </div>

        <div
          class="sidebar__items-container relative [overflow:overlay]"
          :class="isScrolling ? 'before:opacity-100' : 'before:opacity-0'"
          @scroll="handleSidebarScroll"
        >
          <ul class="col relative top-[-10px] pb-4">
            <template v-for="(item, i) in builtMenu">
              <li v-if="!item.children" :key="item.name">
                <router-link
                  :id="item.activeSidebarItemId"
                  :to="item.route"
                  :data-testid="camelCase(item.label)"
                  :class="{
                    'router-link-active active-item': item.activeSidebarItemId === activeItem,
                  }"
                  class="flex rounded-lg px-6 py-4 transition-colors hover:font-bold"
                >
                  <component :is="item.icon" class="me-2 size-6" />
                  <div class="text-body">
                    {{ $t(item.label) }}
                  </div>
                </router-link>
              </li>
              <li
                v-else
                :key="i"
                :class="{
                  'active-tree': hasActiveChild(item),
                }"
              >
                <a
                  href="#"
                  class="flex items-center rounded-lg px-6 py-4"
                  @click.stop.prevent="toggleMenu(item)"
                >
                  <component :is="item.icon" class="me-2 size-6" />
                  <span class="root-tree-label"> {{ $t(item.label) }}</span>
                  <div class="arrow-container ms-auto flex">
                    <fdx-icon-keyboard-arrow-down
                      :class="{
                        'rotate-180': isEntryOpen(item),
                      }"
                      class="size-6 transition-transform"
                    />
                  </div>
                </a>
                <div
                  class="overflow-hidden transition-[height]"
                  :class="isEntryOpen(item) ? 'h-auto' : 'h-0'"
                >
                  <Transition appear name="slide-down">
                    <ul v-if="isEntryOpen(item)" class="sub-menu">
                      <li
                        v-for="child in item.children"
                        :id="child.activeSidebarItemId"
                        :key="child.name"
                        :data-testid="camelCase(child.label)"
                      >
                        <router-link
                          :to="child.route"
                          class="block rounded-lg py-4 pe-4 ps-12 transition-colors hover:font-bold"
                          :class="{
                            'bg-gray-400 font-bold': child.activeSidebarItemId === activeItem,
                          }"
                        >
                          {{ $t(child.label) }}
                        </router-link>
                      </li>
                    </ul>
                  </Transition>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.arrow-container svg {
  @apply !me-0;
}

.sidebar__items-container::before {
  content: '';
  display: block;
  box-shadow: inset 0px 10px 10px -10px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;
  height: 10px;
  width: 100%;
  z-index: 1;
}
</style>
